import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { fetchReviewerDashboard } from '../lib/queries';
import { PageHeader, QueryState } from '../components/ui';
import { Kpi } from '../components/DashboardWidgets';
import { fieldLabel } from '../components/Observations';
import { ReviewQueueTable } from '../components/ReviewQueueTable';
import { PRIORITY_LABEL } from '../lib/reviewQueue';

/**
 * Espacio de trabajo del revisor (`GET /dashboards/revisor`): que tiene por revisar, que espera respuesta de las
 * unidades y que respuestas de las unidades le falta confirmar. Es lo primero que ve un Revisor RAT al entrar.
 */
export function ReviewDashboard() {
  const q = useQuery({ queryKey: ['review', 'dashboard'], queryFn: fetchReviewerDashboard });
  const d = q.data;

  return (
    <>
      <PageHeader
        eyebrow="Revisión"
        title="Tablero del revisor"
        sub="Su trabajo pendiente y lo que está esperando a las unidades"
        actions={
          <Link className="btn" to="/revision/bandeja">
            Ver bandeja completa
          </Link>
        }
      />
      <QueryState isLoading={q.isLoading} error={q.error}>
        {d && (
          <>
            <div className="kpis" style={{ marginBottom: 18 }}>
              <Kpi n={d.resumen.mias} l="Mías" title="Revisiones que usted tomó" />
              <Kpi n={d.resumen.sin_asignar} l="Sin asignar" title="Revisiones libres que puede tomar" />
              <Kpi n={d.resumen.atrasadas} l="Atrasadas" tone={d.resumen.atrasadas > 0 ? 'critical' : undefined} />
              <Kpi n={d.resumen.vencen_24h} l="Vencen en 24 h" tone={d.resumen.vencen_24h > 0 ? 'warning' : undefined} />
            </div>

            <div className="card" style={{ marginBottom: 12 }}>
              <div className="card__b">
                <div className="eyebrow">Cola de revisión</div>
                <p className="muted" style={{ fontSize: 12, margin: '2px 0 8px' }}>
                  {(['3', '2', '1', '0'] as const)
                    .map((p) => `${PRIORITY_LABEL[Number(p) as 0 | 1 | 2 | 3]}: ${d.resumen.por_prioridad[p]}`)
                    .join(' · ')}
                </p>
                <ReviewQueueTable rows={d.cola.slice(0, 10)} emptyText="No hay actividades esperando revisión." />
                {d.cola.length > 10 && (
                  <p style={{ fontSize: 12.5, margin: '8px 0 0' }}>
                    <Link to="/revision/bandeja">Ver las {d.cola.length} tareas →</Link>
                  </p>
                )}
              </div>
            </div>

            <div className="card" style={{ marginBottom: 12 }}>
              <div className="card__b">
                <div className="eyebrow">Respuestas por confirmar</div>
                <p className="muted" style={{ fontSize: 12, margin: '2px 0 8px' }}>
                  La unidad ya respondió estas observaciones; falta que usted las confirme o las reabra.
                </p>
                {d.por_confirmar.length === 0 ? (
                  <p className="muted" style={{ fontSize: 12.5 }}>
                    No hay respuestas pendientes de confirmación.
                  </p>
                ) : (
                  <div className="tablewrap" style={{ border: 0 }}>
                    <table className="data">
                      <thead>
                        <tr>
                          <th>Actividad</th>
                          <th>Campo</th>
                          <th>Tipo</th>
                          <th>Respondida</th>
                        </tr>
                      </thead>
                      <tbody>
                        {d.por_confirmar.map((o) => (
                          <tr key={o.observation_id}>
                            <td>
                              <Link to={`/actividades/${o.activity_id}`} className="mono">
                                {o.ref_code ?? o.activity_id.slice(0, 8)}
                              </Link>
                            </td>
                            <td>{fieldLabel(o.field_path)}</td>
                            <td>
                              <span className={`pill ${o.severity === 'obligatoria' ? 'pill--warning' : 'pill--neutral'}`}>
                                {o.severity === 'obligatoria' ? 'Obligatoria' : 'Sugerida'}
                              </span>
                            </td>
                            <td className="muted">hace {o.respondida_hace_horas} h</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            <div className="card">
              <div className="card__b">
                <div className="eyebrow">Esperando a la unidad</div>
                <p className="muted" style={{ fontSize: 12, margin: '2px 0 8px' }}>
                  Actividades observadas: la pelota está en la unidad.
                </p>
                {d.esperando_a_la_unidad.length === 0 ? (
                  <p className="muted" style={{ fontSize: 12.5 }}>
                    Ninguna actividad está esperando correcciones.
                  </p>
                ) : (
                  <div className="tablewrap" style={{ border: 0 }}>
                    <table className="data">
                      <thead>
                        <tr>
                          <th>Código</th>
                          <th>Actividad</th>
                          <th>Unidad</th>
                          <th>Sin movimiento</th>
                        </tr>
                      </thead>
                      <tbody>
                        {d.esperando_a_la_unidad.map((a) => (
                          <tr key={a.activity_id}>
                            <td className="mono">{a.ref_code ?? '—'}</td>
                            <td>
                              <Link to={`/actividades/${a.activity_id}`}>{a.title}</Link>
                            </td>
                            <td>{a.unit_name}</td>
                            <td>{a.dias === 0 ? 'hoy' : `${a.dias} día${a.dias === 1 ? '' : 's'}`}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </QueryState>
    </>
  );
}
